import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import Toggle from "../../Shared/Toggle";
import TabFormWrapper from "../../Shared/TabFormWrapper";
import FormFooter from "../../Shared/FormFooter";
import AlertRed from "../../Shared/AlertRed";
import AlertGreen from "../../Authentication/AlertGreen";
import AlertModal from "../../Shared/AlertModal";
import BookingPageFormSkeleton from "../../Shared/LoadingSkeletons/BookingPageFormSkeleton";
import {
  useBookingPage,
  useUpdateSettingsTab,
} from "../../../hooks/useBookingPage";

const HideAddressForm = () => {
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [showTurnOffModal, setShowTurnOffModal] = useState(false);
  const [showTurnOnModal, setShowTurnOnModal] = useState(false);

  const { data: configData, isLoading } = useBookingPage();
  const { mutate: updateSettings, isPending: isUpdating } =
    useUpdateSettingsTab();

  const {
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      hideFixedAddressUntilConfirmed: false,
    },
  });

  const hideAddress = watch("hideFixedAddressUntilConfirmed");

  useEffect(() => {
    if (configData?.data) {
      reset(
        { hideFixedAddressUntilConfirmed: configData.data.hideFixedAddressUntilConfirmed ?? false },
        { keepDirty: false }
      );
    }
  }, [configData, reset]);

  const handleToggle = (checked) => {
    if (!checked) {
      // Turning OFF — show warning modal before persisting
      setShowTurnOffModal(true);
      return;
    }
    // Turning ON — explain the consequences before persisting
    setShowTurnOnModal(true);
  };

  const confirmTurnOn = () => {
    setValue("hideFixedAddressUntilConfirmed", true, { shouldDirty: true });
    setShowTurnOnModal(false);
    setErrors({});
    setSuccess(false);
  };

  const dismissTurnOnModal = () => {
    setShowTurnOnModal(false);
  };

  const confirmTurnOff = () => {
    setValue("hideFixedAddressUntilConfirmed", false, { shouldDirty: true });
    setShowTurnOffModal(false);
    setErrors({});
    setSuccess(false);
  };

  const dismissTurnOffModal = () => {
    setShowTurnOffModal(false);
  };

  const performSave = (data) => {
    setErrors({});
    setSuccess(false);

    updateSettings(data, {
      onSuccess: () => {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      },
      onError: (err) => {
        const errorMessage = err.response?.data?.error?.message;
        const errorDetails = err.response?.data?.error?.details;

        let displayMessage =
          errorMessage || "Failed to save address visibility settings";

        if (errorDetails && Array.isArray(errorDetails)) {
          displayMessage = errorDetails.join(", ");
        }

        setErrors({ title: "Error", message: displayMessage });
      },
    });
  };

  if (isLoading) {
    return (
      <TabFormWrapper>
        <BookingPageFormSkeleton showToggle={true} inputCount={0} />
      </TabFormWrapper>
    );
  }

  return (
    <>
      <TabFormWrapper onSubmit={handleSubmit(performSave)}>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-medium text-ink-900">
                Hide your location's address until you approve
              </h3>
              <p className="text-sm text-ink-500">
                Clients see your location's name, not the address, until you
                approve their booking.
              </p>
            </div>
            <Toggle checked={hideAddress} onChange={handleToggle} />
          </div>
        </div>

        <FormFooter
          primaryCaption="Save"
          isLoading={isUpdating}
          disabled={!isDirty}
          requiresVerification={false}
        />

        {errors.title && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertRed title={errors.title} message={errors.message} />
          </div>
        )}

        {success && (
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <AlertGreen
              title="Success"
              message="Address visibility settings saved successfully"
            />
          </div>
        )}
      </TabFormWrapper>

      <AlertModal
        isOpen={showTurnOnModal}
        modalTitle="Turn on address privacy?"
        modalMessage={
          <ul className="list-disc space-y-2 pl-5">
            <li>
              Clients see your location's name, not the address, until you
              approve their booking.
            </li>
            <li>
              Every booking will wait for your approval before it's confirmed.
            </li>
            <li>Auto-approval turns off while this is on.</li>
            <li>
              Your location name is shown publicly — don't put the street
              address there.
            </li>
          </ul>
        }
        primaryCaption="Turn on"
        secondaryCaption="Cancel"
        primaryClickHandler={confirmTurnOn}
        dismissModalHandler={dismissTurnOnModal}
        primaryButtonColour="blue"
        showIcon={false}
      />

      <AlertModal
        isOpen={showTurnOffModal}
        modalTitle="Show your address?"
        modalMessage="Turning this off makes your location's address visible on your public booking page."
        primaryCaption="Show address"
        secondaryCaption="Keep hidden"
        primaryClickHandler={confirmTurnOff}
        dismissModalHandler={dismissTurnOffModal}
        primaryButtonColour="red"
      />
    </>
  );
};

export default HideAddressForm;
